import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  styled,
  TextField,
  Typography,
  CircularProgress,
  Autocomplete,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadIcon from '../../assets/svg/upload.svg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import toast from 'react-hot-toast';
import getAxiosInstance from 'api';

const businessRegistrationOptions = [
  'Private Limited Company',
  'Public Limited Company',
  'Limited Liability Partnership',
  'Sole Trader',
  'Partnership',
  'Charity / Non-profit',
  'Other',
];

const DropBoxStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px dashed #F0F0F0',
  borderRadius: theme.shape.borderRadius,
  height: '120px',
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: '#FCFCFC',
  color: '#9E9E9E',
  marginBottom: '0px',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#F5F5F5',
  },
  overflow: 'hidden',
  position: 'relative',
}));

const FormGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(0, 3),
  borderRight: '1px solid rgba(33, 33, 32, 0.1)',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 0),
    borderRight: '1px solid transparent',
  },
}));

const UploadFileGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 0),
  },
}));

const FileUpload = ({ label, onFileUploaded, authToken, field }) => {
  const MAX_SIZE = 10 * 1024 * 1024;
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = async (selectedFile) => {
    if (selectedFile.size > MAX_SIZE) {
      toast.error('File size exceeds 10MB.');
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('field', field);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_EDOC_API_URL}/businessPartner/uploadKYCDocument`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const data = await response.json();
      const url = data.data;
      setFile(selectedFile);
      onFileUploaded(url);
    } catch (err) {
      console.error(err);
      setError('Failed to upload file. Please try again.');
      toast.error('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleRemoveFile = () => {
    setFile(null);
    onFileUploaded('');
  };

  const renderFilePreview = () => {
    if (!file) return null;

    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        width="100%"
        height="100%"
        position="relative"
      >
        {isImage ? (
          <img
            src={URL.createObjectURL(file)}
            alt={file.name}
            style={{ maxHeight: '100%', maxWidth: '100%' }}
          />
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            {isPdf ? (
              <PictureAsPdfIcon color="error" />
            ) : (
              <DescriptionIcon color="action" />
            )}
            <Typography variant="h3" noWrap>
              {file.name}
            </Typography>
          </Stack>
        )}
        <IconButton
          onClick={handleRemoveFile}
          size="small"
          sx={{
            position: 'absolute',
            top: 5,
            right: 5,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <Stack gap={1}>
      <Typography variant="h4">{label}</Typography>
      <DropBoxStyled
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        component="label"
      >
        {!file && !uploading ? (
          <Box display={'flex'} flexDirection="column" alignItems="center">
            <img src={UploadIcon} alt="Upload" height={28} />
            <Typography variant="Body_medium_16">
              Drag and Drop files here, or{' '}
              <Typography variant="Body_medium_16" color="secondary">
                Browse
              </Typography>
            </Typography>
            <input
              type="file"
              hidden
              onChange={handleFileChange}
              accept=".pdf, .doc, .docx, .jpeg, .jpg, .png"
            />
          </Box>
        ) : uploading ? (
          <CircularProgress />
        ) : (
          renderFilePreview()
        )}
      </DropBoxStyled>
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{
          color: 'rgba(33, 33, 32, 0.4)',
        }}
      >
        <Typography variant="Body_light_12">
          Supports: PDF, Word, JPEG, PNG
        </Typography>
        <Typography variant="Body_light_12">Maximum size: 10MB</Typography>
      </Stack>
      {error && (
        <Typography variant="Body_light_12" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  );
};

const StatusIcon = ({ status }) => {
  if (status === 'loading')
    return <CircularProgress size={18} color="secondary" />;
  if (status === 'valid')
    return <CheckCircleIcon color="success" fontSize="small" />;
  if (status === 'invalid')
    return <CancelIcon color="error" fontSize="small" />;
  return null;
};

const KYC = () => {
  const [certificateUrl, setCertificateUrl] = useState('');
  const [directorIdUrl, setDirectorIdUrl] = useState('');
  const [confirmationStatementUrl, setConfirmationStatementUrl] = useState('');
  const [utilityBillUrl, setUtilityBillUrl] = useState('');
  const [directorIdNumber, setDirectorIdNumber] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [idType, setIdType] = useState('PASSPORT');
  const [validationStatus, setValidationStatus] = useState({});
  const [directorDetails, setDirectorDetails] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);

  const [businessRegistrationType, setBusinessRegistrationType] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [website, setWebsite] = useState('');

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const reviewStatus = localStorage.getItem('reviewStatus');

  useEffect(() => {
    setDirectorIdNumber('');
    setDirectorDetails(null);
    setValidationStatus((p) => ({ ...p, [idType]: 'invalid' }));
  }, [idType]);

  const handleSubmit = () => {
    if (
      !businessRegistrationType ||
      !businessDescription ||
      !website ||
      !certificateUrl ||
      !directorIdUrl ||
      !confirmationStatementUrl ||
      !utilityBillUrl ||
      !companyAddress
    ) {
      toast.error('Please fill all fields and upload all required files.');
      return;
    }

    // UK: no external identity verification — admin reviews fields + docs.
    if (!directorIdNumber?.trim()) {
      toast.error('Please enter director passport / driving licence number.');
      return;
    }
    if (!companyNumber?.trim()) {
      toast.error('Please enter Companies House number.');
      return;
    }

    const data = {
      businessRegistrationType,
      organizationType: businessRegistrationType,
      businessDescription,
      website,
      companyNumber,
      companyAddress,
      registeredAddress: companyAddress,
      certificateOfIncorporationUrl: certificateUrl,
      directorIdUrl,
      identityDocumentUrl: confirmationStatementUrl,
      utilityBillUrl,
      bpKYCData: {
        ...(companyDetails || {}),
        ...(directorDetails || {}),
        directorIdType: idType,
        directorIdNumber,
        companyNumber,
        verificationMode: 'admin-review',
      },
    };

    fetch(`${process.env.REACT_APP_EDOC_API_URL}/businessPartner/updateKYC`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userData?.accessToken}`,
        'journey-type': 'kyc-company',
        'app-name': process.env.REACT_APP_NAME,
        'app-version': process.env.REACT_APP_VERSION,
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((result) => {
        if (!(result?.codes === 200 || result?.success)) {
          toast.error(result?.message || 'An error occurred');
          return;
        }
        localStorage.setItem('reviewStatus', 'KYC_PENDING');
        toast.success('KYC submitted successfully!');
        setTimeout(() => {
          window.location.reload();
        }, 200);
      })
      .catch((error) => {
        console.error(error);
        toast.error('Failed to submit KYC. Please try again.');
      });
  };

  const handleCancel = () => {
    setBusinessRegistrationType('');
    setBusinessDescription('');
    setWebsite('');
    setCertificateUrl('');
    setDirectorIdUrl('');
    setConfirmationStatementUrl('');
    setUtilityBillUrl('');
    setCompanyNumber('');
    setCompanyAddress('');
    setDirectorIdNumber('');
    setDirectorDetails(null);
    setCompanyDetails(null);
    setValidationStatus({});
  };

  const validateField = async (field, payload) => {
    setValidationStatus((p) => ({ ...p, [field]: 'loading' }));

    getAxiosInstance(
      `businessPartner/verifyKYCDetails`,
      'post',
      {
        country: 'GBR',
        verificationLevel: 'basic',
        ...payload,
      },
      {
        Authorization: `Bearer ${userData?.accessToken}`,
      }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setValidationStatus((p) => ({ ...p, [field]: 'valid' }));
          if (field === 'PASSPORT' || field === 'DRIVING_LICENCE') {
            setDirectorDetails(res?.data?.data);
          } else {
            setCompanyDetails(res?.data?.data);
          }
        } else {
          setValidationStatus((p) => ({ ...p, [field]: 'invalid' }));
          if (field === 'PASSPORT' || field === 'DRIVING_LICENCE') {
            setDirectorDetails(null);
          } else {
            setCompanyDetails(null);
          }
        }
      })
      .catch((err) => {
        setValidationStatus((p) => ({ ...p, [field]: 'invalid' }));
        toast.error(err?.message || 'Verification failed');
        setDirectorDetails(null);
        setCompanyDetails(null);
      });
  };

  return (
    <Box className="page-content">
      <Container>
        <Grid container>
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: '12px' }}>
              <Stack gap={1}>
                <Box mb={1.5}>
                  <Typography variant="h3">
                    👋 Hello{' '}
                    <Typography variant="Body_medium_18" color="secondary">
                      {userData?.name || userData?.companyName || 'Partner'}!
                    </Typography>
                  </Typography>
                </Box>

                <Typography variant="h2">Welcome to E-DOC Online</Typography>

                <Typography
                  variant="Body_light_14"
                  fontWeight={400}
                  sx={{
                    color: 'rgba(33, 33, 32, 0.6)',
                  }}
                >
                  We will assist you to connect to millions of financial
                  accounts through our direct banking connection, recurring bank
                  payments with a single, secure Open Banking API and verifying
                  right candidates for your needs.
                </Typography>
              </Stack>
            </Card>
          </Grid>

          {reviewStatus === 'KYC_PENDING' && (
            <Grid item xs={12} my={3}>
              <Card sx={{ p: 3, borderRadius: '12px' }}>
                <Typography variant="h3" gutterBottom>
                  KYC under review
                </Typography>
                <Typography variant="Body_light_14" sx={{ color: 'rgba(33, 33, 32, 0.6)' }}>
                  Your documents have been submitted. An administrator will
                  review your application shortly.
                </Typography>
              </Card>
            </Grid>
          )}

          {reviewStatus !== 'KYC_PENDING' && reviewStatus !== 'KYC_ACCEPTED' && (
            <Grid item xs={12} my={3}>
              <Card sx={{ p: 3, borderRadius: '12px' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h3">
                      Please complete your KYC
                    </Typography>
                  </Grid>

                  <Grid item xs={12} mt={1} mb={3}>
                    <Divider />
                  </Grid>

                  <FormGrid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Stack gap={1}>
                          <Typography variant="h4">
                            Business Registration Type
                          </Typography>
                          <Autocomplete
                            fullWidth
                            options={businessRegistrationOptions}
                            value={businessRegistrationType}
                            onChange={(event, newValue) =>
                              setBusinessRegistrationType(newValue)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Enter business registration type"
                              />
                            )}
                          />
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <Stack gap={1}>
                          <Typography variant="h4">
                            Business Description
                          </Typography>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="text"
                            placeholder="Write a description..."
                            multiline
                            rows={4}
                            value={businessDescription}
                            onChange={(e) =>
                              setBusinessDescription(e.target.value)
                            }
                          />
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <Stack gap={1}>
                          <Typography variant="h4">Website</Typography>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="url"
                            placeholder="https://"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                          />
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <Stack gap={1}>
                          <Typography variant="h4">
                            Director ID (Passport / Driving Licence)
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <TextField
                                select
                                fullWidth
                                value={idType}
                                onChange={(e) => setIdType(e.target.value)}
                              >
                                <MenuItem value="PASSPORT">Passport</MenuItem>
                                <MenuItem value="DRIVING_LICENCE">
                                  Driving Licence
                                </MenuItem>
                              </TextField>
                            </Grid>
                            <Grid item xs={8}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                type="text"
                                placeholder={`Enter ${
                                  idType === 'PASSPORT'
                                    ? 'passport'
                                    : 'driving licence'
                                } number`}
                                value={directorIdNumber}
                                onChange={(e) =>
                                  setDirectorIdNumber(e.target.value)
                                }
                                onBlur={() => {
                                  if (!directorIdNumber) return;
                                  validateField(idType, {
                                    type: idType,
                                    country: 'GBR',
                                    verificationLevel: 'basic',
                                    directorIdNumber,
                                    directorIdType: idType,
                                  });
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {directorIdNumber && (
                                        <StatusIcon
                                          status={validationStatus[idType]}
                                        />
                                      )}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                              {directorDetails?.verified && (
                                <Typography
                                  variant="Body_medium_14"
                                  color="secondary"
                                  component="span"
                                >
                                  Director ID accepted for review
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h4">
                          Companies House Number
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Companies House Number"
                          value={companyNumber}
                          onChange={(e) => setCompanyNumber(e.target.value)}
                          onBlur={() => {
                            if (!companyNumber) return;
                            validateField('COMPANY', {
                              type: 'COMPANY',
                              country: 'GBR',
                              verificationLevel: 'basic',
                              companyNumber,
                              companyName: userData?.companyName,
                            });
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <StatusIcon status={validationStatus.COMPANY} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        {companyDetails?.companyName && (
                          <>
                            Company Name:-{' '}
                            <Typography
                              variant="Body_medium_14"
                              color="secondary"
                              component="span"
                            >
                              {companyDetails.companyName}
                            </Typography>
                          </>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <Stack gap={1}>
                          <Typography variant="h4">Company Address</Typography>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="text"
                            placeholder="Write an address..."
                            rows={4}
                            multiline
                            value={companyAddress}
                            onChange={(e) => setCompanyAddress(e.target.value)}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </FormGrid>

                  <UploadFileGrid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FileUpload
                          label="Upload Certificate of Incorporation"
                          onFileUploaded={setCertificateUrl}
                          authToken={userData?.accessToken}
                          field="certificateOfIncorporation"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FileUpload
                          label="Upload Director ID"
                          onFileUploaded={setDirectorIdUrl}
                          authToken={userData?.accessToken}
                          field="directorId"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FileUpload
                          label="Upload Confirmation Statement"
                          onFileUploaded={setConfirmationStatementUrl}
                          authToken={userData?.accessToken}
                          field="confirmationStatement"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FileUpload
                          label="Upload Utility Bill"
                          onFileUploaded={setUtilityBillUrl}
                          authToken={userData?.accessToken}
                          field="utilityBillUrl"
                        />
                      </Grid>
                    </Grid>
                  </UploadFileGrid>

                  <Grid item xs={12} my={1}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12}>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'flex-end'}
                      gap={1}
                    >
                      <Button
                        variant="contained"
                        onClick={handleCancel}
                        sx={{
                          backgroundColor: 'common.white',
                          color: 'common.black',
                          border: '1px solid rgba(33, 33, 32, 0.2)',
                          ':hover': {
                            backgroundColor: '#f4f4f4',
                            color: 'common.black',
                            border: '1px solid #f4f4f4',
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleSubmit}
                      >
                        Confirm
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default KYC;
